import { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("ga_token");
    if (!token) {
      setUser(false);
      setReady(true);
      return;
    }
    // 12h idle limit: if the session was kept and inactive for 12h+, sign out.
    const keep = localStorage.getItem("ga_keep") !== "0";
    const last = Number(localStorage.getItem("ga_last_active") || 0);
    if (keep && last && Date.now() - last > 12 * 60 * 60 * 1000) {
      localStorage.removeItem("ga_token");
      localStorage.removeItem("ga_last_active");
      setUser(false);
      setReady(true);
      return;
    }
    localStorage.setItem("ga_last_active", String(Date.now()));
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => {
        localStorage.removeItem("ga_token");
        setUser(false);
      })
      .finally(() => setReady(true));
  }, []);

  const persist = (data, keep = true) => {
    localStorage.setItem("ga_token", data.token);
    localStorage.setItem("ga_last_active", String(Date.now()));
    localStorage.setItem("ga_keep", keep ? "1" : "0");
    setUser(data.user);
  };

  const login = async (email, password, keep = true) => {
    const { data } = await api.post("/auth/login", { email, password });
    persist(data, keep);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    persist(data, payload.keep_signed_in !== false);
    return data.user;
  };

  const firebaseLogin = async (idToken, trustDevice = true) => {
    const { data } = await api.post("/auth/firebase/session", { id_token: idToken, trust_device: trustDevice });
    persist(data, trustDevice);
    return data;
  };

  const socialpulseLogin = async (payload) => {
    const { data } = await api.post("/auth/socialpulse/exchange", payload);
    persist(data, payload.trust_device !== false);
    return data;
  };

  const handoffLogin = async (token) => {
    const { data } = await api.post("/auth/handoff/redeem", { token });
    if (data.status === "ok") persist({ token: data.token, user: data.user }, true);
    return data;
  };

  const magicRequest = async (email, edition = "global") => {
    const { data } = await api.post("/auth/magic/request", { email, edition });
    return data;
  };

  const magicVerify = async (token, keep = false, name) => {
    const { data } = await api.post("/auth/magic/verify", { token, name });
    persist(data, keep);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem("ga_token");
    localStorage.removeItem("ga_last_active");
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, ready, login, register, firebaseLogin, socialpulseLogin, handoffLogin, magicRequest, magicVerify, logout, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
